import { DRAFT_SECTION_SENTINEL } from './draft-sentinel';

/**
 * Counter-signal for the section marker strippers: does a marker the
 * presentation serializers are supposed to remove still appear in the payload
 * heading for the clipboard or the exporter? Detection only.
 *
 * Scoped deliberately narrow. A note body can legitimately *contain* marker
 * text — Ask Heidi echoes the note back when asked to reformat it, and a
 * clinician can paste one — so a string scan cannot prove a stripper failed,
 * only that a marker is present. Every rule below is therefore restricted to
 * the position a stripper failure actually produces:
 *
 * - the comment markers only when they occupy a whole line or block, which
 *   keeps quoted mentions (`` `<!-- section-end -->` ``) unflagged;
 * - the draft sentinel only inside a section title, matching how the stripper
 *   scopes its own removal — a zero-width space in body text is pasted content.
 *
 * Rules that could not be positioned this way are intentionally absent: the
 * `<section data-section-title>` wrapper (AI-echoed note HTML and fenced code
 * examples are indistinguishable from residue), every entity-escaped twin, and
 * the `&amp;nbsp;` blank-line marker.
 */

export type MarkerResidueFlavor = 'html' | 'text';
export type MarkerResidueKind =
  | 'draft_sentinel'
  | 'empty_section_heading'
  | 'pipeline_sentinel'
  | 'section_end';

export type MarkerResiduePayload = {
  flavor: MarkerResidueFlavor;
  value: string;
};

/**
 * Mid-conversion swaps in the legacy Showdown pipeline
 * (`src/helpers/markdownToHtml.ts`). Not a section marker, but never valid
 * output on any path, and nothing authors them — so no positional guard.
 */
const PIPELINE_SENTINELS = /ABAEMPTYLINEABA|ABACODEBLOCKABA|ABAHTMLTAGABA/;

/** A comment marker standing alone between two block tags, or at either edge. */
const htmlBlockMarker = (body: string): RegExp =>
  new RegExp(`(?:^|>)\\s*<!--\\s*${body}\\s*-->\\s*(?:<|$)`, 'i');

/**
 * A marker inside a code element is being *shown* to the reader, not emitted
 * by a serializer — `<code>` closes with `>`, so it would otherwise read as a
 * block boundary. Elements are emptied rather than removed so the surrounding
 * boundaries stay intact.
 */
const withoutHtmlCodeContent = (html: string): string =>
  html.replace(/(<(code|pre)\b[^>]*>)[\s\S]*?(<\/\2>)/gi, '$1$3');

type ResidueRule = {
  kind: MarkerResidueKind;
  /** Which clipboard/export flavor the pattern is residue in. */
  appliesTo: MarkerResidueFlavor | 'both';
  /** No `g` flag anywhere: `.test` on a global regex is stateful across calls. */
  pattern: RegExp;
};

const RESIDUE_RULES: readonly ResidueRule[] = [
  // Persisted as its own heading line, so the `#` prefix is part of the shape.
  {
    kind: 'empty_section_heading',
    appliesTo: 'text',
    pattern: /^ {0,3}(?:#[ \t]+)?<!--\s*section-heading-empty(?::\d+)?\s*-->[ \t\r]*$/m,
  },
  {
    kind: 'empty_section_heading',
    appliesTo: 'html',
    pattern: htmlBlockMarker('section-heading-empty(?::\\d+)?'),
  },
  {
    kind: 'section_end',
    appliesTo: 'text',
    pattern: /^[ \t]*<!--\s*section-end\s*-->[ \t\r]*$/m,
  },
  { kind: 'section_end', appliesTo: 'html', pattern: htmlBlockMarker('section-end') },
  // Scoped to an ATX H1 title, as the strippers scope removal: a zero-width
  // space in body text or an `##`–`#####` content heading is pasted content.
  {
    kind: 'draft_sentinel',
    appliesTo: 'text',
    pattern: new RegExp(`^ {0,3}#[ \\t][^\\n]*${DRAFT_SECTION_SENTINEL}`, 'm'),
  },
  // Bounded rather than `[^<]*`, because the sentinel can sit behind a nested
  // tag (`<h1><em>Plan</em>` then the sentinel) and the stripper removes it
  // anywhere in the title; the `</h1>` guard keeps the scan out of the body.
  {
    kind: 'draft_sentinel',
    appliesTo: 'html',
    pattern: new RegExp(
      `<h1\\b[^>]*>(?:(?!</h1>)[\\s\\S]){0,400}?${DRAFT_SECTION_SENTINEL}`,
      'i'
    ),
  },
  { kind: 'pipeline_sentinel', appliesTo: 'both', pattern: PIPELINE_SENTINELS },
];

/** `marker_kind` is sorted so the Datadog facet is order-independent. */
export const detectSectionMarkerResidue = (
  payloads: readonly MarkerResiduePayload[]
): { found: boolean; marker_kind: MarkerResidueKind[] } => {
  const kinds = new Set<MarkerResidueKind>();

  for (const { flavor, value: raw } of payloads) {
    if (!raw) continue;
    const value = flavor === 'html' ? withoutHtmlCodeContent(raw) : raw;
    for (const rule of RESIDUE_RULES) {
      if (rule.appliesTo !== 'both' && rule.appliesTo !== flavor) continue;
      if (rule.pattern.test(value)) kinds.add(rule.kind);
    }
  }

  return { found: kinds.size > 0, marker_kind: [...kinds].sort() };
};
